/**
 * Checks whether or not a value is numeric.
 * @param n
 * @returns {boolean}
 */
import { isPlainObject } from "lodash";
import { applicationContext } from "../services/main/ApplicationContext";

class HTTPError extends Error {
	name;
	response;
	status;

	constructor(response) {
		super(response.statusText);
		this.name = "HTTPError";
		this.response = response;
		this.status = response.status;
	}

	/**
	 * Usually thrown when user is not logged in.
	 * @returns {boolean}
	 */
	isUnauthorized = () => this.status === 401;

	/**
	 * Usually thrown when user has insufficient permissions.
	 * @returns {boolean}
	 */
	isForbidden = () => this.status === 403;
}

export const isNumeric = (n) => {
	return !isNaN(parseFloat(n)) && isFinite(n);
};

/**
 * Performs fetch on an external resource and checks status.
 * @param url
 * @param opts
 * @returns {Promise<Response>}
 */
export const fetchExternalResource = async (url, opts = {}) => {
	if (isPlainObject(opts.body)) {
		if (!opts.headers) {
			opts.headers = {};
		}

		if (!opts.headers["Content-Type"]) {
			opts.headers["Content-Type"] = "application/json;charset=utf-8";
		}

		opts.body = JSON.stringify(opts.body);
	}

	opts.mode = "cors";

	const result = await fetch(url, opts);

	if (result.ok) {
		return result;
	} else {
		throw new HTTPError(result);
	}
};

/**
 * Performs fetch on local resource.
 * @param url
 * @param opts
 * @returns {Promise<Response>}
 */
export const fetchResource = (url, opts = {}) => {
	return fetchExternalResource(
		`${process.env.INSURFORCE_BASE_API_URL}${url}`,
		opts
	);
};

/**
 * Performs authenticated fetch on local resource.
 * @param url
 * @param opts
 * @returns {Promise<Response>}
 */
export const fetchProtectedResource = (url, opts = {}) => {
	if (!opts.headers) {
		opts.headers = {};
	}

	if (!opts.headers["Authorization"]) {
		opts.headers["Authorization"] = `Bearer ${applicationContext.authToken}`;
	}

	return fetchResource(url, opts);
};

/**
 * Calculates today's age from given year.
 * @param dateString
 * @returns {number}
 */
export const getAge = (dateString) => {
	let today = new Date();
	let birthDate = new Date(dateString);
	let age = today.getFullYear() - birthDate.getFullYear();
	let m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}
	return age;
};

export const defaultNumberFormat = (number, opts = {}) => {
	if (!isNumeric(number)) {
		return number;
	}

	if (!opts.minimumFractionDigits && !opts.maximumFractionDigits) {
		opts.minimumFractionDigits = 2;
		opts.maximumFractionDigits = 2;
	}

	return new Intl.NumberFormat("en-GB", opts).format(parseFloat(number));
};

export const defaultCurrencyFormat = (number, currency = "GBP") => {
	return defaultNumberFormat(number, {
		style: "currency",
		currency,
		currencyDisplay: "symbol",
	});
};

/**
 * Object with all rule definitions for form rules.
 * @type {{required: {message: string, required: boolean}}}
 */
export const formRules = {
	email: {
		type: "email",
		message: "The input is not valid E-mail!",
	},
	required: {
		required: true,
		message: "This field is required!",
	},
	number: {
		type: "number",
		message: "This field should be a number",
	},
	gtZero: {
		validator: (rule, value, callback) => {
			if (value <= 0) {
				callback(1);
				return;
			}
			callback();
		},
		message: "This value should be greater than 0.",
	},
};

/**
 * Object with the default form layout definition.
 * @type {{wrapperCol: {sm: {span: number}, xs: {span: number}}, labelCol: {sm: {span: number}, xs: {span: number}}}}
 */
export const formLayout = {
	labelCol: {
		xs: { span: 24 },
		sm: { span: 12 },
	},
	wrapperCol: {
		xs: { span: 24 },
		sm: { span: 12 },
	},
};
